import {mergeDeepRight} from 'ramda';
import React, {useMemo, useState} from 'react';
import {
  View,
  TextInput,
  FlatList,
  TouchableOpacity,
  Image,
  Pressable,
  ViewStyle,
  ImageStyle,
  TextStyle,
} from 'react-native';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {Checkbox, Button, Text} from 'ui-kits';

type modes = 'standalone' | 'popup';
type propType = Readonly<{
  selectedEntries: any[];
  searchPlaceholder: string;
  renderOption: (item: any) => JSX.Element;
  searchKeyExtractor: (item: any) => string;
  optionKeyExtractor: (item: any) => string;
  onSave: (savedOptions: any[]) => void;
  onClose: () => void;
  mode?: modes;
  options: any[];
  singleSelect?: boolean;
  style?: ViewStyle;
  header?: string;
  fetchNextData?: () => void;
}>;

export function SearchDropDown(props: propType) {
  const {
    selectedEntries = [],
    searchPlaceholder,
    renderOption,
    onClose,
    onSave,
    searchKeyExtractor,
    options = [],
    optionKeyExtractor,
    mode = 'popup',
    singleSelect = false,
    style: overrideStyle,
    header = '',
    fetchNextData,
  } = props;
  const [searchText, setSearchText] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, boolean>
  >(
    selectedEntries.reduce(
      (acc: any, item: any) => ({
        ...acc,
        [optionKeyExtractor(item)]: true,
      }),
      {},
    ) || {},
  );
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  function handleChangeSearchText(text: string) {
    setSearchText(text);
  }

  function handleClear() {
    if (!singleSelect) {
      setIsSelectedAll(false);
    }
    setSelectedOptions({});
  }

  function handleSelectAll(value: boolean) {
    if (singleSelect) {
      return;
    }
    // value then select all
    if (value) {
      setSelectedOptions(p => {
        const p_clone = structuredClone(p);
        for (let option of options) {
          p_clone[optionKeyExtractor(option)] = true;
        }
        return p_clone;
      });
    }
    setIsSelectedAll(value); // toggle
  }

  function toggleSelectOption(id: string) {
    if (singleSelect) {
      setSelectedOptions(p => ({[id]: !p[id]}));
      return;
    }
    // maintaining object of slected id's, instead of array,
    // better for lookup for selected option
    // and store value
    setSelectedOptions(p => {
      const p_clone = structuredClone(p);
      const isOptionSelect = p_clone[id] || false;
      p_clone[id] = !isOptionSelect;
      return p_clone;
    });
  }

  function handleSave() {
    let savedOptions = [];
    for (let option of options) {
      if (selectedOptions[optionKeyExtractor(option)]) {
        savedOptions.push(option);
      }
    }
    onSave(savedOptions);
  }

  // filtering data at local
  const filterdOptions = useMemo<any[]>(() => {
    return options.filter(item => {
      const searchKey = searchKeyExtractor(item).toLowerCase();
      const search = searchText.replace(/ /g, '').toLowerCase();
      return searchKey.includes(search);
    });
  }, [searchText, options, searchKeyExtractor]);

  const styles = mergeDeepRight(baseStyles, modeStyles[mode]) as styleProps;
  return (
    <View style={[styles.dropdownContainer, overrideStyle]}>
      {header ? <Text style={styles.header}>{header}</Text> : null}
      {/* search input  */}
      <View style={styles.searchBarWrapper}>
        {/* search icon */}
        <Image
          source={require('assets/icons/search@2x.png')}
          style={styles.icon}
        />
        {/* search text input */}
        <TextInput
          style={styles.searchBar}
          placeholder={searchPlaceholder}
          placeholderTextColor={colors.palette.secondaryBlack5}
          value={searchText}
          onChangeText={handleChangeSearchText}
        />
        {/* showing cross icon when value is there in the search */}
        {searchText ? (
          <TouchableOpacity onPress={() => setSearchText('')}>
            <Image
              source={require('assets/icons/cross@2x.png')}
              style={styles.icon}
            />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* action label (do not render in standAlone mode) */}
      {mode === 'popup' || !singleSelect ? (
        <View style={styles.actionLabels}>
          {/* clear all */}
          <Pressable onPress={handleClear}>
            <Text>Clear all</Text>
          </Pressable>
          {!singleSelect && (
            <View style={styles.selectAllWrapper}>
              <Text>Select all</Text>
              <Checkbox checked={isSelectedAll} onChange={handleSelectAll} />
            </View>
          )}
        </View>
      ) : null}

      {/* rendering options */}

      <FlatList
        showsVerticalScrollIndicator={true}
        data={filterdOptions}
        keyExtractor={item => optionKeyExtractor(item)}
        onEndReached={() => {
          if (searchText){
            return;
          }
          fetchNextData?.();
        }}
        renderItem={({item}) => {
          const id = optionKeyExtractor(item);
          return (
            <View style={styles.option}>
              {/* render prop JSX */}
              <View style={styles.renderItem}> {renderOption?.(item)} </View>

              {/* checkbox to select that item */}
              <Checkbox
                checked={selectedOptions[id] || false}
                onChange={() => toggleSelectOption(id)}
              />
            </View>
          );
        }}
      />

      {/* footer, action buttons, and number of selected options */}
      <View style={styles.footer}>
        {/* selected number label */}
        <Text style={styles.selectNumberLabel}>
          {`${
            Object.keys(selectedOptions).filter(item => selectedOptions[item])
              .length
          }/${options.length}`}
        </Text>

        {/* action button */}
        <View style={styles.actionButtonsWrapper}>
          {mode === 'popup' ? (
            <Button
              onPress={onClose}
              text="cancel"
              variant="secondary"
              style={styles.actionButton}
              textStyle={styles.actionButtonText}
            />
          ) : null}
          <Button
            onPress={handleSave}
            text="Ok"
            style={styles.actionButton}
            textStyle={styles.actionButtonText}
          />
        </View>
      </View>
    </View>
  );
}

type styleProps = {
  dropdownContainer: ViewStyle;
  searchBarWrapper: ViewStyle;
  searchBar: TextStyle;
  option: ViewStyle;
  profilePic: ImageStyle;
  actionButtonsWrapper: ViewStyle;
  actionButton: ViewStyle;
  actionButtonText: TextStyle;
  actionLabels: ViewStyle;
  footer: ViewStyle;
  icon: ImageStyle;
  renderItem: ViewStyle;
  selectAllWrapper: ViewStyle;
  selectNumberLabel: TextStyle;
  header: TextStyle;
};

const baseStyles: styleProps = {
  header: {
    fontSize: 16,
    fontWeight: '600',
  },
  selectAllWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing['1'],
  },
  selectNumberLabel: {color: colors.palette.secondaryBlack5, fontSize: 12},
  renderItem: {flex: 1},
  dropdownContainer: {
    backgroundColor: 'white',
    borderRadius: corner_radius['rounded-md'],
    gap: spacing['2'],
    bottom: 0,
    right: 0,
  },
  actionLabels: {
    marginTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 500,
    marginRight: spacing[1],
    color: colors.palette.secondaryBlack5,
  },
  searchBarWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing['2'],
    borderRadius: corner_radius['rounded-md'],
    paddingRight: 8,
  },
  searchBar: {
    padding: 10,
    outlineWidth: 0,
    width: '100%',
    minWidth: 0,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing['2'],
    paddingRight: spacing['1'],
  },

  profilePic: {
    width: 40,
    height: 40,
    borderRadius: 40,
    marginRight: 12,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionButtonsWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
    gap: spacing['4'],
  },
  actionButton: {maxWidth: 60, height: 30},
  actionButtonText: {fontSize: 12},
  icon: {
    width: 14,
    height: 14,
  },
};

const modeStyles: Record<modes, Partial<styleProps>> = {
  popup: {
    dropdownContainer: {
      padding: spacing[2],
      maxHeight: 300,
      position: 'absolute',
      zIndex: 999,
      minWidth: 250,
      boxShadow: '0px 0px 10px 2px rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
      borderColor: colors.palette.stroke,
    },
    searchBarWrapper: {
      backgroundColor: colors.palette.gray,
    },
    searchBar: {
      backgroundColor: colors.palette.gray,
      outlineColor: colors.palette.gray,
    },
  },
  standalone: {
    dropdownContainer: {
      width: '100%',
    },
    searchBarWrapper: {
      backgroundColor: colors.palette.white,
      borderColor: colors.palette.stroke,
      borderWidth: 1,
    },
    searchBar: {
      backgroundColor: colors.palette.white,
      outlineColor: colors.palette.white,
    },
  },
};
